/**
 * Expected vs Confirmed VAT Adjustments (Підпорція 3.1b, 2026-05-24).
 *
 * Ручне коригування ПДВ-кредиту/зобов'язань. 4 типи коригування:
 *  - REGISTERED_WITH_DIFFERENCE — ПН зареєстрована з помилкою.
 *  - NOT_REGISTERED — ПН не зареєстрована → reverse кредиту.
 *  - CUSTOMS_RECALC — митниця перерахувала.
 *  - OTHER — інше (audit-only).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { evcva, type EVCVA, type AdjustmentType } from '@/api/vatAdjustments';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

type TypeFilter = 'ALL' | AdjustmentType;

const TYPE_LABEL: Record<AdjustmentType, string> = {
  REGISTERED_WITH_DIFFERENCE: 'Зареєстрована з різницею',
  NOT_REGISTERED: 'Не зареєстрована',
  CUSTOMS_RECALC: 'Митниця перерахувала',
  OTHER: 'Інше',
};

export default function EVCVAPage() {
  const navigate = useNavigate();
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('ALL');

  const list = useQuery({
    queryKey: ['evcva', { typeFilter }],
    queryFn: () =>
      evcva.list({
        limit: 200,
        ...(typeFilter !== 'ALL' ? { adjustment_type: typeFilter } : {}),
      }),
  });

  const cols = useMemo<ColumnDef<EVCVA>[]>(
    () => [
      { key: 'document_number', header: '№', width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      {
        key: 'document_date',
        header: 'Дата',
        width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => (r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : ''),
      },
      {
        key: 'adjustment_type',
        header: 'Тип',
        width: 200,
        sortValue: (r) => TYPE_LABEL[r.adjustment_type] ?? r.adjustment_type,
        cell: (r) => TYPE_LABEL[r.adjustment_type] ?? r.adjustment_type,
      },
      { key: 'adjustment_reason', header: 'Причина', sortValue: (r) => r.adjustment_reason ?? '', cell: (r) => r.adjustment_reason },
      {
        key: 'expected_amount',
        header: 'Очікувано',
        width: 140,
        align: 'right',
        sortValue: (r) => Number(r.expected_amount),
        cell: (r) => r.expected_amount,
      },
      {
        key: 'confirmed_amount',
        header: 'Підтверджено',
        width: 140,
        align: 'right',
        sortValue: (r) => Number(r.confirmed_amount),
        cell: (r) => r.confirmed_amount,
      },
      {
        key: 'difference',
        header: 'Різниця',
        width: 140,
        align: 'right',
        sortValue: (r) => Number(r.difference),
        cell: (r) => r.difference,
      },
      {
        key: 'posted',
        header: 'Проведено',
        width: 120,
        align: 'center',
        sortValue: (r) => (r.posted ? 1 : 0),
        cell: (r) => (r.posted ? '✓ так' : 'ні'),
      },
    ],
    [],
  );

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Коригування ПДВ</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label style={{ fontSize: '0.875rem' }}>Тип:</label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
            data-testid="evcva-type-filter"
            style={{ width: 140, flex: '0 0 auto' }}
          >
            <option value="ALL">Усі</option>
            <option value="REGISTERED_WITH_DIFFERENCE">Зареєстровані з різницею</option>
            <option value="NOT_REGISTERED">Не зареєстровані</option>
            <option value="CUSTOMS_RECALC">Митниця перерахувала</option>
            <option value="OTHER">Інше</option>
          </select>
        </div>
        <button
          className="primary"
          onClick={() => navigate('/taxation/expected-confirmed-vat-adj/new')}
          style={{ marginLeft: 'auto' }}
        >
          + Нове
        </button>
      </div>
      <DataTable<EVCVA>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/taxation/expected-confirmed-vat-adj/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
